import type { Reward } from '@anidex/shared/economy';
import { useTranslation } from 'react-i18next';
import type { AchievementDTO } from '@anidex/shared/types';
import { ProgressMeter } from '../../components/Badges';
import { AnimaAmount, FragmentIcon } from '../../components/Brand';
import { UnlockMark } from '../../components/UnlockMark';
import { achievementDescription, achievementTitle, boosterShortName } from '../../i18n/catalog';
import { formatNumber, rarityLabel } from '../../services/format';

const PACKS = [
  { type: 'standard', name: 'Standard' },
  { type: 'premium', name: 'Premium' },
  { type: 'legendary', name: 'Chanceux' },
] as const;

function PrizePack({ type, name, count }: { type: (typeof PACKS)[number]['type']; name: string; count: number }) {
  const { t } = useTranslation('play');
  const label = t('rewards.booster', { ns: 'common', count, name: boosterShortName(type, name) });
  return (
    <span className={`prize-pack prize-pack--${type}`}>
      <span className="prize-pack__crimp" aria-hidden="true" />
      <span className="prize-pack__foil" aria-hidden="true" />
      {count > 1 && (
        <span className="prize-pack__count" aria-hidden="true">
          ×{count}
        </span>
      )}
      <span className="visually-hidden">{label}</span>
    </span>
  );
}

export function AchievementPrize({ reward, characterName }: { reward: Reward; characterName?: string | null }) {
  const { t } = useTranslation('play');
  const fragmentNote = reward.fragments
    ? characterName
      ? t('forName', { name: characterName })
      : reward.fragmentsChoice
        ? t('choiceCard')
        : null
    : null;
  return (
    <div className="prize-shelf">
      {reward.anima ? <AnimaAmount value={reward.anima} size={22} signed /> : null}
      {reward.xp ? (
        <span className="prize-xp">
          +{formatNumber(reward.xp)}
          <span className="visually-hidden"> {t('achievements.xp')}</span>
        </span>
      ) : null}
      {reward.fragments ? (
        <span className="prize-fragments">
          <FragmentIcon size={18} />+{formatNumber(reward.fragments)}
        </span>
      ) : null}
      {PACKS.map((pack) => {
        const count = reward.boosters?.[pack.type] ?? 0;
        if (count <= 0) return null;
        return <PrizePack key={pack.type} type={pack.type} name={pack.name} count={count} />;
      })}
      {reward.characterRarity ? (
        <span className={`prize-character rarity-${reward.characterRarity}`}>
          {characterName && !reward.fragments
            ? characterName
            : t('rewards.character', { ns: 'common', rarity: rarityLabel(reward.characterRarity) })}
        </span>
      ) : null}
      {reward.unlocks?.map((unlock) => (
        <UnlockMark key={`${unlock.kind}:${unlock.key}`} kind={unlock.kind} label={unlock.label} unlockKey={unlock.key} />
      ))}
      {fragmentNote ? <span className="prize-note">{fragmentNote}</span> : null}
    </div>
  );
}

export function AchievementRow({
  achievement,
  claiming,
  onClaim,
}: {
  achievement: AchievementDTO;
  claiming: boolean;
  onClaim: (id: string) => void;
}) {
  const { t } = useTranslation('play');
  const title = achievementTitle(achievement.id);
  const ready = achievement.claimable > 0;
  const span = Math.max(1, achievement.target - achievement.previousTarget);
  const done = Math.min(span, Math.max(0, achievement.progress - achievement.previousTarget));
  return (
    <li className={`achievement${ready ? ' achievement--ready' : ''}`}>
      <div className="achievement__copy">
        <p className="achievement__title">
          {title}
          <span className="achievement__tier">{t('achievements.tier', { tier: achievement.tier + 1 })}</span>
        </p>
        <p className="achievement__description">{achievementDescription(achievement.id, achievement.target)}</p>
        <div className="quest__track">
          <ProgressMeter value={ready ? span : done} max={span} label={t('progress', { title })} />
          <span className="quest__count">
            {formatNumber(Math.min(achievement.progress, achievement.target))} / {formatNumber(achievement.target)}
          </span>
        </div>
        {achievement.tier > 0 ? (
          <p className="achievement__date">{t('achievements.claimedTiers', { count: achievement.tier })}</p>
        ) : null}
      </div>
      <div className="achievement__side">
        <AchievementPrize reward={achievement.reward} />
        {ready ? (
          <button type="button" className="button button--primary button--small" disabled={claiming} onClick={() => onClaim(achievement.id)}>
            {achievement.claimable > 1
              ? t('achievements.claimMany', { count: achievement.claimable })
              : t('achievements.claim')}
          </button>
        ) : null}
      </div>
    </li>
  );
}
